// client/src/components/pages/LexiStar/Dashboard/metricFormat.ts
//
// Formatting for semantic-layer values (ST-00188 M4-D; m4-contracts §3 units:
// km, L, L/100km, t, t·km, h, %, ZAR, count, min). The screens never compute a
// KPI (m4-contracts §1 "figures are deterministic") — they only format what the
// resolver returned and compare it with the previous period the API sent.
// Numbers group with a comma and use a decimal point, as the control-tower
// pages do (StatusBoard/format.js). Percentages arrive as 0–100.

export type Unit = 'km' | 'L' | 'L/100km' | 't' | 't·km' | 'h' | '%' | 'ZAR' | 'count' | 'min' | string;

/** Which way is better for a metric: up (tonnes, on-route %), down (idle, cost), or neither. */
export type Better = 'up' | 'down' | 'neutral';

export const isNum = (v: unknown): v is number | string =>
  v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

/** Grouped with a comma, decimal point, and a true minus sign (U+2212) for negatives. */
const group = (n: number, digits: number): string =>
  n.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits }).replace(/^-/, '−');

/** Digits that keep small values honest and large ones calm. */
const autoDigits = (n: number): number => {
  const a = Math.abs(n);
  if (a === 0) return 0;
  if (a < 10) return 1;
  return 0;
};

/** 1,284 · 12.9k · 4.2m (compact from 100,000 up; used for tiles on narrow screens). */
export function compact(n: number): string {
  const a = Math.abs(n);
  const sign = n < 0 ? '−' : '';
  if (a >= 1e9) return `${sign}${(a / 1e9).toFixed(1)}bn`;
  if (a >= 1e6) return `${sign}${(a / 1e6).toFixed(1)}m`;
  if (a >= 1e5) return `${sign}${(a / 1e3).toFixed(0)}k`;
  return group(n, autoDigits(n));
}

/** "1h 23m" / "48 min" / "3d 4h". */
export function fmtMinutes(min: number): string {
  const m = Math.round(min);
  if (Math.abs(m) < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${String(m % 60).padStart(2, '0')}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** The number alone (no unit), for big tile figures. */
export function formatNumber(value: unknown, unit: Unit, { compactLarge = false } = {}): string {
  if (!isNum(value)) return '—';
  const n = Number(value);
  switch (unit) {
    case '%':
      return group(n, 1);
    case 'ZAR':
      return `${n < 0 ? '−' : ''}R${compactLarge ? compact(Math.abs(n)) : group(Math.abs(n), Math.abs(n) < 100 ? 2 : 0)}`;
    case 'min':
      return fmtMinutes(n);
    case 'L/100km':
      return group(n, 1);
    case 'h':
      return group(n, Math.abs(n) < 100 ? 1 : 0);
    case 't':
      return compactLarge ? compact(n) : group(n, Math.abs(n) < 100 ? 1 : 0);
    case 'count':
      return compactLarge && Math.abs(n) >= 1e5 ? compact(n) : group(n, 0);
    default:
      return compactLarge ? compact(n) : group(n, autoDigits(n));
  }
}

/** The unit as shown after a number ('' when the number carries it: R, min, count). */
export function unitSuffix(unit: Unit): string {
  switch (unit) {
    case 'ZAR':
    case 'min':
    case 'count':
      return '';
    case 'L/100km':
      return 'L/100 km';
    default:
      return unit;
  }
}

/** Number + unit: "1,284 L", "R12,400", "92.4%", "1h 05m", "341 trips" (count takes no unit). */
export function formatValue(value: unknown, unit: Unit, opts?: { compactLarge?: boolean }): string {
  const num = formatNumber(value, unit, opts);
  if (num === '—') return num;
  const suffix = unitSuffix(unit);
  if (!suffix) return num;
  return unit === '%' ? `${num}%` : `${num} ${suffix}`;
}

export interface Delta {
  /** Signed text: "+12%", "−3.1 pts", "+R4,200", "no change", "new". */
  text: string;
  direction: 'up' | 'down' | 'flat';
  /** 'better' | 'worse' when the metric has a preferred direction. */
  judgement: 'better' | 'worse' | null;
}

const MINUS = '−';

/**
 * Change against the previous period. Percent metrics move in percentage
 * points; everything else as a relative change, or absolute when the previous
 * value was zero (a relative change from nothing is meaningless).
 */
export function deltaOf(current: unknown, previous: unknown, unit: Unit, better: Better): Delta | null {
  if (!isNum(current) || !isNum(previous)) return null;
  const c = Number(current);
  const p = Number(previous);
  const diff = c - p;
  const direction: Delta['direction'] =
    Math.abs(diff) < 1e-9 ? 'flat' : diff > 0 ? 'up' : 'down';
  const judgement =
    direction === 'flat' || better === 'neutral'
      ? null
      : (direction === 'up') === (better === 'up')
        ? 'better'
        : 'worse';
  const sign = diff > 0 ? '+' : diff < 0 ? MINUS : '';
  let text: string;
  if (direction === 'flat') text = 'no change';
  else if (unit === '%') text = `${sign}${group(Math.abs(diff), 1)} pts`;
  else if (p === 0) text = `${sign}${formatValue(Math.abs(diff), unit)}`;
  else {
    const rel = (Math.abs(diff) / Math.abs(p)) * 100;
    text = `${sign}${group(rel, rel < 10 ? 1 : 0)}%`;
  }
  return { text, direction, judgement };
}
